// ── Live arrow counter — session model (M9.5 Phase 2, #1813) ────────────────
//
// Pure, side-effect-light model for the burst-tap counter's in-progress
// session. Deliberately framework-free (no React) so every rule here —
// undo-as-a-unit, started_at/ended_at derivation, ~90s end clustering,
// overnight staleness — is unit-testable without mounting a component.
//
// A session is `{ increments: [{ t, n }] }`: one entry per user ACTION (a
// single +1 tap, or one +3/+6/+N chip tap), `t` an epoch-ms timestamp and `n`
// the arrows that action added. This is the same shape the backend's
// `tap_log` column stores (see backend/app/models.py ArrowLog).
//
// started_at / ended_at fall out of the increments array for free:
// `increments[0].t` / `increments[last].t`. Undoing back to zero increments
// therefore resets "started_at" with no special-case code — the next tap
// just becomes increments[0] again.
import { localISODate } from "./dates";
import {
  safeLocalStorageGet,
  safeLocalStorageSet,
  safeLocalStorageRemove,
} from "./storage";

const KEY_PREFIX = "xring.counter.session.";

// A tap within this many ms of the previous action is treated as a duplicate
// event (e.g. a pointer + touch event both firing for one physical tap), NOT
// a fast intentional second tap — burst-tapping itself is never filtered.
export const DEDUPE_MS = 50;

// UI-only clustering for the gold burst chip — how long it lingers after the
// last tap before fading. Separate constant from the ~90s END_GAP_MS below;
// conflating the two would make the chip's fade timing double as (wrong)
// end-boundary logic.
export const BURST_LINGER_MS = 1500;

// End-boundary inference — deliberately different from BURST_LINGER_MS.
export const END_GAP_MS = 90_000;

// Idle-dim threshold — no taps for this long and the UI recedes.
export const IDLE_DIM_MS = 45_000;

function keyFor(archerId) {
  return `${KEY_PREFIX}${archerId ?? "anon"}`;
}

export function emptySession() {
  return { increments: [] };
}

function sanitize(session) {
  if (!session || !Array.isArray(session.increments)) return emptySession();
  const increments = session.increments.filter(
    (e) => e && typeof e.t === "number" && typeof e.n === "number" && e.n > 0,
  );
  return { increments };
}

export function loadSession(archerId) {
  const raw = safeLocalStorageGet(keyFor(archerId));
  if (!raw) return emptySession();
  try {
    return sanitize(JSON.parse(raw));
  } catch {
    // Malformed stored JSON — start clean rather than crash the counter.
    return emptySession();
  }
}

export function saveSession(archerId, session) {
  // Storage unavailable (private mode, quota) — the session just won't resume.
  safeLocalStorageSet(keyFor(archerId), JSON.stringify(sanitize(session)));
}

export function clearSession(archerId) {
  safeLocalStorageRemove(keyFor(archerId));
}

// A single +1 tap. Applies the dedupe window against the immediately
// preceding action (of any kind) — a true duplicate event is dropped
// silently (same session reference returned, no increment added).
export function addTap(session, t) {
  const s = sanitize(session);
  const last = s.increments[s.increments.length - 1];
  if (last && t - last.t < DEDUPE_MS) return s;
  return { increments: [...s.increments, { t, n: 1 }] };
}

// A whole-end chip (+3 / +6 / +N) or the numeric prompt — one deliberate
// action, so no dedupe window (unlike addTap, back-to-back chip taps are
// always intentional).
export function addChip(session, n, t) {
  const s = sanitize(session);
  if (!(n > 0)) return s;
  return { increments: [...s.increments, { t, n }] };
}

// Removes the LAST ACTION as a whole unit — undoing a +6 chip removes all 6,
// not 1, because undo pops one increments[] entry, not one arrow.
export function undoLast(session) {
  const s = sanitize(session);
  if (!s.increments.length) return s;
  return { increments: s.increments.slice(0, -1) };
}

export function totalCount(session) {
  return sanitize(session).increments.reduce((sum, e) => sum + e.n, 0);
}

export function startedAt(session) {
  const s = sanitize(session);
  return s.increments.length ? s.increments[0].t : null;
}

export function endedAt(session) {
  const s = sanitize(session);
  return s.increments.length ? s.increments[s.increments.length - 1].t : null;
}

// Approximate end count from tap-timing clusters — a NEW cluster starts
// whenever the gap since the previous action exceeds END_GAP_MS. Display-only:
// never fed into stats aggregation (see Help.jsx / ArrowLogCard.jsx).
export function approxEndsCount(session, gapMs = END_GAP_MS) {
  const { increments } = sanitize(session);
  if (!increments.length) return 0;
  let ends = 1;
  for (let i = 1; i < increments.length; i++) {
    if (increments[i].t - increments[i - 1].t > gapMs) ends++;
  }
  return ends;
}

// Raw tap events in the `tap_log` shape the backend stores verbatim.
export function buildTapLog(session) {
  return sanitize(session).increments.map(({ t, n }) => ({ t, n }));
}

export function formatDuration(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.round(ms / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// A session is "stale" once the calendar day has rolled over since the LAST
// tap — the overnight case #1814 exists to catch. Empty sessions are never
// stale (there's nothing to auto-finish).
export function isStale(session, now = new Date()) {
  const ended = endedAt(session);
  if (ended == null) return false;
  return localISODate(new Date(ended)) !== localISODate(now);
}

// "18:04" — 24-hour, zero-padded. Local wall-clock, no timezone conversion:
// the archer is standing at the range right now, so their device's own clock
// is definitionally correct for this session.
export function hhmm(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

// Meta line under the hero total. Before the first tap there is no
// started_at yet, so it reads the instructional copy instead of a stat line.
export function metaLineText(session, now = Date.now()) {
  const s = sanitize(session);
  if (!s.increments.length) return "Timer starts on your first tap";
  const ends = approxEndsCount(s);
  const elapsed = formatDuration(now - startedAt(s)) ?? "0m";
  return `${ends} end${ends === 1 ? "" : "s"} · ${elapsed} · started ${hhmm(startedAt(s))}`;
}

// "18:04 – 19:32 · 1h 28m" — the save sheet's TIME row, built from the
// session's ACTUAL first/last tap, never "now".
export function timeRangeText(session) {
  const s = startedAt(session);
  const e = endedAt(session);
  if (s == null || e == null) return "";
  return `${hhmm(s)} – ${hhmm(e)} · ${formatDuration(e - s) ?? "0m"}`;
}

// "150 arrows · ~14 ends" — the save sheet's unit line under the editable
// total. `count` is the (possibly archer-edited) override, not necessarily
// totalCount(session) — the approx-ends figure still comes from the real tap
// clusters regardless of a count correction.
export function saveSheetUnitLine(session, count) {
  const ends = approxEndsCount(session);
  return `arrow${count === 1 ? "" : "s"} · ~${ends} end${ends === 1 ? "" : "s"}`;
}

// Combines a "YYYY-MM-DD" date with an "HH:MM" wall-clock time into an epoch
// ms timestamp — used when the save sheet's TIME row is edited, so a changed
// clock time is reconstructed against the (possibly also-edited) date row.
export function combineDateAndTime(dateStr, timeStr) {
  const [y, mo, d] = String(dateStr).split("-").map(Number);
  const [h, mi] = String(timeStr).split(":").map(Number);
  if (![y, mo, d, h, mi].every(Number.isFinite)) return null;
  return new Date(y, mo - 1, d, h, mi).getTime();
}

export { keyFor as _keyForTesting };
